import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  forwardRef,
} from 'react'
import { useRouteMatch, useLocation, useHistory } from 'react-router-dom'
import { DesignContext } from 'contexts/DesignContext'
import { HistoryContext } from 'contexts/HistoryContext'
import { ConfigContext } from 'contexts/ConfigContext'
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles'
import objectPath from 'object-path'
import { useTimeFormat } from 'contexts/TimeFormatContext'

// action
import { createTableStyle } from './table/Action'

import { isEmpty } from '../action/condition/State'
import { COLUMN_SETTING } from './table/config'
import { getEditComponent, getRenderColumn } from './table/action/Column'
import { generateString } from '../action/Data'
import cloneDeep from 'clone-deep'
import { checkWithDefaultValue, genRefId, getRowId } from './table/action/index'
import Table from './table/Table'
import { Button } from 'react-bootstrap'
import { applyMaterialTableColumnType } from './table/helper/TableColumnType'

const FavoriteTable = (props) => {
  let { uischema, title, config } = props
  let { favoriteDataKey, updateValue, mapFieldFavorite = [] } = config
  const history = useHistory()
  const match = useRouteMatch()
  const location = useLocation()
  let receivePathParams = match.params
  let urlSearchParams = new URLSearchParams(location.search || [])
  let receiveQueryParams = Object.fromEntries([...urlSearchParams])
  let allParams = { ...receivePathParams, ...receiveQueryParams }
  const designContext = useContext(DesignContext)
  const historyContext = useContext(HistoryContext)
  const configContext = useContext(ConfigContext)
  const { layoutType } = configContext.state
  const { formatDate, formatTime, formatDateTime } = useTimeFormat()

  const { jsonForm, isUiChanged, employeeList, getEmployee } = designContext
  const columnRefId = objectPath.get(uischema, 'loadData.columnRefId')

  let formData = jsonForm.formData
  const [tableColumn, setTableColumn] = useState([])
  const [columnRequire, setColumnRequire] = useState([])

  const [loading, setLoading] = useState(false)
  const [modalAction, setModalAction] = useState({})
  const [isClickButton, setIsClickButton] = useState(false)

  const [render, setRender] = useState(false)
  const [focus, setFocus] = useState({})
  const tableRef = useRef()
  const tableColumnList = objectPath.get(uischema, 'tableColumn') || []
  const editFavorite = isUiChanged?.editFavorite || {}
  const tableData = objectPath.get(editFavorite, `jsonData.${favoriteDataKey}`)
  const tableForm =
    objectPath.get(formData, uischema.key) ||
    objectPath.get(formData, uischema.name)

  const setTableData = (tableData) => {
    updateValue('jsonData', {})
    updateValue(`jsonData.${favoriteDataKey}`, tableData)
    setRender(true)
  }

  useEffect(() => {
    if (!tableData) {
      setTableData(tableForm)
    }
  }, [])

  let localization = {
    toolbar: {
      searchPlaceholder: uischema?.tableAction?.searchLabel ?? 'ค้นหา',
    },
  }

  // ======== TABLE OPTIONAL ACTION ======== //

  let colorText = layoutType === 'dark' ? '#adb7be' : '#495057' + ' !important'
  let colorPagination =
    layoutType === 'dark'
      ? 'rgba(255, 255, 255, 0.54)'
      : 'rgba(0, 0, 0, 0.54)' + ' !important'

  const tableTheme = createTheme({
    overrides: {
      MuiMenu: {
        paper: {
          backgroundColor: 'white',
        },
      },
      MuiPaper: {
        root: {
          backgroundColor: 'transparent',
          maxWidth: uischema.maxWidth || '100%',
          '& [class*="Component-horizontalScrollContainer-"]': {
            backgroundColor: 'transparent',
            color: colorText,
            display: 'inline-grid',
            width: '100%',
          },
        },
      },
      MuiToolbar: {
        regular: {
          display: title || uischema?.tableAction?.searchable ? 'flex' : 'none',
        },
      },
      MuiTableRow: {
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: uischema.rowColor1 || 'transparent',
            color: uischema.rowTextColor1 || colorText,
            '& [class*="MuiTableCell-body-"]': {
              color: uischema.rowTextColor1 || colorText,
            },
          },
          '&:nth-of-type(even)': {
            backgroundColor: uischema.rowColor2 || 'transparent',
            color: uischema.rowTextColor2 || colorText,
            '& [class*="MuiTableCell-body-"]': {
              color: uischema.rowTextColor2 || colorText,
            },
          },
          '& [class*="ForwardRef(MTableHeaderRef)-header-"]': {
            backgroundColor: 'transparent',
            color: colorText,
          },
          '& [class*="Mui-disabled"]': {
            cursor: 'default',
            pointerEvents: 'auto',
          },
        },
      },
      MuiTableHead: {
        root: {
          '& [class*="MuiTableRow-"]': {
            backgroundColor: uischema.headerColor + ' !important',
          },
          '& [class*=" MuiTableCell-head"]': {
            color: uischema.headerTextColor + ' !important',
            fontWeight: uischema.headerBold ? 'bold' : 'normal' + ' !important',
          },
        },
      },
      MuiTableFooter: {
        root: {
          '& [class*="MuiTableRow-"]': {
            backgroundColor: 'transparent !important',
            color: colorText,
          },
          '& [class*="MuiToolbar-"]': {
            display: 'flex',
            backgroundColor: 'transparent !important',
            color: colorText,
          },
          '& [class*="ForwardRef(MTablePaginationRef)-"]': {
            backgroundColor: 'transparent !important',
            color: colorText,
          },
          '& [class*="MuiIconButton-"]': {
            backgroundColor: 'transparent !important',
            color: colorPagination,
          },
        },
      },
      MuiList: {
        root: {
          backgroundColor: 'transparent',
          color: colorText,
        },
      },
    },
  })

  const numberTheme = createTheme({
    overrides: {
      MuiInputBase: {
        input: {
          fontSize: '14px',
        },
      },
    },
  })

  const currencyTheme = createTheme({
    overrides: {
      MuiInputBase: {
        input: {
          fontSize: '14px',
          textAlign: 'end',
        },
      },
    },
  })

  // ======== RENDER TABLE COLUMN ======== //

  const onRowTableAdd = (newData) => {
    return new Promise((resolve) => {
      setLoading(true)
      let genId = generateString(10)
      objectPath.set(newData, genRefId, genId)
      let dataUpdate = cloneDeep(tableData)
      newData = checkWithDefaultValue(newData, uischema)
      objectPath.set(newData, 'tableData.no', dataUpdate.length + 1)
      dataUpdate.push(newData)
      setTableData(dataUpdate)
      resolve()
    }).then(() => {
      setTimeout(() => {
        setLoading(false)
      }, 100)
    })
  }

  const onRowTableUpdate = (newData, oldData) => {
    return new Promise((resolve) => {
      setLoading(true)
      let index = -1
      let dataUpdate = cloneDeep(tableData)
      let genId = objectPath.get(oldData, genRefId)
      let rowId = getRowId(oldData, uischema)
      newData = checkWithDefaultValue(newData, uischema)
      if (columnRefId && rowId) {
        index = dataUpdate.findIndex((data) => {
          return objectPath.get(data, columnRefId) === rowId
        })
      } else if (genId) {
        index = dataUpdate.findIndex((data) => {
          return objectPath.get(data, genRefId) === genId
        })
      } else {
        index = dataUpdate.findIndex((data) => {
          let isCheckTableNo =
            objectPath.get(data, 'tableData.no') ===
            objectPath.get(oldData, 'tableData.no')
          if (isCheckTableNo) return true
          let foundUnique = Object.keys(data).find((key) => {
            if (key === 'tableData') return false
            return objectPath.get(data, key) !== objectPath.get(oldData, key)
          })
          if (foundUnique) return false
          return true
        })
      }
      if (index > -1) {
        objectPath.set(
          newData,
          'tableData',
          objectPath.get(oldData, 'tableData'),
        )
        objectPath.set(dataUpdate, index, newData)
      }
      setTableData(dataUpdate)
      resolve()
    }).then(() =>
      setTimeout(() => {
        setLoading(false)
      }, 100),
    )
  }

  const onRowTableDelete = (oldData) => {
    return new Promise((resolve) => {
      setLoading(true)
      let index = -1
      let dataUpdate = cloneDeep(tableData)
      let genId = objectPath.get(oldData, genRefId)
      let rowId = getRowId(oldData, uischema)
      if (columnRefId && rowId) {
        index = dataUpdate.findIndex((data) => {
          return objectPath.get(data, columnRefId) === rowId
        })
      } else if (genId) {
        index = dataUpdate.findIndex((data) => {
          return objectPath.get(data, genRefId) === genId
        })
      } else {
        index = dataUpdate.findIndex((data) => {
          let isCheckTableNo =
            objectPath.get(data, 'tableData.no') ===
            objectPath.get(oldData, 'tableData.no')
          if (isCheckTableNo) return true
          let foundUnique = Object.keys(data).find((key) => {
            if (key === 'tableData') return false
            return objectPath.get(data, key) !== objectPath.get(oldData, key)
          })
          if (foundUnique) return false
          return true
        })
      }
      if (index > -1) {
        objectPath.del(dataUpdate, index)
        dataUpdate.map((rowData, index) =>
          objectPath.set(rowData, 'tableData.no', index + 1),
        )
        setTableData(dataUpdate)
      }
      resolve()
    }).then(() =>
      setTimeout(() => {
        setLoading(false)
      }, 100),
    )
  }

  useEffect(() => {
    if (render) {
      setRender(false)
    }
  }, [render])

  useEffect(() => {
    let hasEmployee = tableColumnList.find(
      ({ type }) => type === 'userId' || type === 'employeeCode',
    )
    if (jsonForm?.formCode && employeeList.length === 0 && hasEmployee) {
      getEmployee()
    }
  }, [jsonForm?.formCode])

  useEffect(() => {
    if (!tableColumnList) return []
    let newColumn = []

    let filterColumn = tableColumnList.filter(
      ({ field, typeField }) =>
        typeField !== 'system' &&
        mapFieldFavorite.find((item) => item.field === field),
    )
    let detailColumns = {}
    filterColumn.forEach((column, index) => {
      let type = column?.typeField || column?.type
      let valueType = column?.valueType
      if (valueType === 'element' && column?.receiveKey) {
        column.value = objectPath.get(formData, column.receiveKey)
      }
      if (type === 'detailPanel') {
        objectPath.set(detailColumns, column.field, column.detailColumn)
        return
      }
      if (type === 'html') {
        let formatType = objectPath.get(column, 'html.formatType')
        if (formatType === 'detail') {
          objectPath.set(detailColumns, column.field, column)
          return
        }
      }
      const stateParent = {
        tableData,
        setTableData,
        focus,
        setFocus,
        modalAction,
        setModalAction,
        isClickButton,
        setIsClickButton,
        columnRequire,
        setColumnRequire,
      }
      const defaultProp = {
        uischema,
        column,
        designContext,
        focus,
        tableRef,
        formData,
        allParams,
        location,
        history,
        historyContext,
      }
      const dateTimeProp = { formatDate, formatTime, formatDateTime }
      const actionProp = {}
      const themeProp = { numberTheme, currencyTheme }

      if (type && objectPath.get(COLUMN_SETTING, [type, 'editable'])) {
        column.editable = objectPath.get(COLUMN_SETTING, [type, 'editable'])
      }
      if (type && objectPath.get(COLUMN_SETTING, [type, 'editComponent'])) {
        column.editComponent = getEditComponent({
          defaultProp,
          dateTimeProp,
          stateParent,
          themeProp,
          actionProp,
        })
      }
      if (type && objectPath.get(COLUMN_SETTING, [type, 'render'])) {
        column.render = getRenderColumn({
          defaultProp,
          dateTimeProp,
          stateParent,
          themeProp,
          actionProp,
        })
      }

      if (column?.isRequired) {
        column.validate = (rowData) => {
          let value = objectPath.get(rowData, column.field)
          return isEmpty(value) ? { isValid: false } : true
        }
        if (!columnRequire.includes(column.field))
          columnRequire.push(column.field)
      }
      if (column?.field === uischema?.sortField) {
        column.defaultSort = uischema.defaultSort || 'asc'
        column.sorting = true
      } else {
        delete column.defaultSort
      }
      column = applyMaterialTableColumnType(column, type)
      column = createTableStyle(column, uischema)
      objectPath.set(column, 'tableData.columnOrder', index)
      newColumn.push(column)
    })

    setTableColumn(newColumn)
    setColumnRequire(columnRequire)
  }, [
    JSON.stringify(tableData),
    JSON.stringify(tableColumnList),
    uischema.rowPadding,
    uischema.defaultSort,
    uischema.sortField,
  ])

  return (
    <>
      <div
        style={{
          maxHeight: uischema.maxHeight || '100%',
          minHeight: uischema.minHeight || 'auto',
          overflowY: 'auto',
          boxShadow: '1px 1px 6px 0px #d7d7d7',
        }}
      >
        <MuiThemeProvider theme={tableTheme}>
          <Table
            isLoading={loading}
            title={'Favorite'}
            columns={tableColumn}
            data={tableData}
            localization={localization}
            tableIcons={{
              Add: forwardRef((_, ref) => (
                <Button ref={ref} variant="outline-primary">
                  <i className="feather icon-plus-square mr-1"></i>
                  เพิ่ม Data
                </Button>
              )),
            }}
            editable={{
              onRowAdd: (newData) => onRowTableAdd(newData),
              onRowUpdate: (newData, oldData) =>
                onRowTableUpdate(newData, oldData),
              onRowDelete: (oldData) => onRowTableDelete(oldData),
            }}
          />
        </MuiThemeProvider>
      </div>
    </>
  )
}

export default FavoriteTable
